// The Objekt as a record: its create/edit/delete/restore flows, its listings,
// and the filters over them. Field labels are NOT here — `livingArea`,
// `marketPrice`, `address` and the rest resolve through `useField(key)`.
//
// "Objekt" is the entity throughout: a PROPERTY can be a flat or a plot, so
// "Immobilie" is reserved for marketing prose.
import { COMMON } from './common'
import { FIELD_LABELS } from './fields'
import { MATCHING } from './matching'
import { TERMS } from './terms'

const LISTINGS = 'Inserate'

export const PROPERTY = {
  // Headings of PROPERTY_FIELD_GROUPS — they name groups of fields, not fields.
  sections: {
    building: TERMS.building,
    energy: 'Energie & Technik',
    outdoor: 'Außenbereich',
    other: COMMON.other,
    features: 'Ausstattung',
  },
  // The two pins on the energy scale: where the property stands today and
  // where the renovation puts it. Abbreviated because they sit under a bar
  // barely wider than the words.
  energyScale: {
    current: 'IST',
    target: 'ZIEL',
  },
  create: `${TERMS.property} erstellen`,
  edit: `${TERMS.property} bearbeiten`,
  editAddress: 'Adresse bearbeiten',
  // Example URLs the staff member replaces.
  listingUrlPlaceholder: 'https://www.immobilienscout24.de/…',
  imageUrlPlaceholder: 'https://example.com/bild.jpg',
  // Stands in wherever the record carries no name of its own — image alt text,
  // a card heading, a list row.
  genericName: TERMS.property,
  imageAltNumbered: (title: string, index: number) => `${title} – ${index}`,
  streetViewAlt: 'Aufnahme aus Google Street View',
  noAddress: 'Keine Adresse hinterlegt',
  updateAddress: 'Adresse aktualisieren',
  regenerateCoordinates: 'Koordinaten neu generieren',
  coordinatesRegenerated: 'Koordinaten wurden aktualisiert',
  coordinatesRegenerationFailed:
    'Koordinaten konnten nicht aktualisiert werden',
  imageUrl: 'Bild-URL',
  roomsAbbr: 'Zi.',
  // The customer's search criteria, written out. The price band always names
  // what it includes: the figure the customer compares against a listing is
  // the purchase price, and theirs is the purchase price plus the renovation.
  criteria: {
    priceRange: (from: string, to: string) =>
      `${from} bis ${to} (inkl. Sanierung)`,
    priceUpTo: (to: string) => `${COMMON.to} ${to} (inkl. Sanierung)`,
    priceFrom: (from: string) => `${COMMON.from} ${from} (inkl. Sanierung)`,
  },
  search: 'Suche nach Adresse, Stadt …',
  empty: 'Noch keine Objekte angelegt.',
  noResults: 'Keine Objekte gefunden',
  allTypes: 'Alle Objektarten',
  allEnergyLabels: 'Alle Energieeffizienzklassen',
  selectType: 'Objektart wählen',
  selectSubType: 'Unterart wählen',

  addListing: 'Inserat hinzufügen',
  addListingDescription: 'Externes Inserat zu diesem Objekt hinzufügen.',
  listingUrl: 'Inserat-URL',
  listingUrlNotHttps: 'Die Inserat-URL muss mit https:// beginnen.',
  typeStrandsSelections: (count: number) =>
    count === 1
      ? 'Eine Antwort im Konfigurator hat im Baum des neuen Objekttyps keinen Platz. Sie muss entfernt werden, bevor sich der Typ ändern lässt.'
      : `${count} Antworten im Konfigurator haben im Baum des neuen Objekttyps keinen Platz. Sie müssen entfernt werden, bevor sich der Typ ändern lässt.`,
  listingAdded: 'Inserat wurde hinzugefügt',
  listingAddError: 'Inserat konnte nicht hinzugefügt werden.',
  listings: LISTINGS,
  /** Both ways a property reaches a buyer, on one screen. */
  listingsAndMatches: `${LISTINGS} & ${MATCHING.matches.label}`,
  listingPrice: 'Kaufpreis der Immobilie',
  viewFullListing: 'Vollständiges Inserat ansehen',
  source: TERMS.source,
  viewOn: 'Ansehen auf',

  delete: {
    title: `${TERMS.property} löschen`,
    message:
      'Dieses Objekt wird gelöscht und alle zugehörigen Matches werden gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
    matchWarning: (n: number) =>
      `Achtung: Dieses Objekt hat ${n} Matches mit Kunden. Durch das Löschen werden alle Matches unwiderruflich gelöscht.`,
    matchWarningFromMatch: (n: number) =>
      `Achtung: Dieses Objekt hat ${n} weitere Matches mit anderen Kunden. Durch das Löschen werden alle Matches unwiderruflich gelöscht.`,
    permanentTitle: `${TERMS.property} endgültig löschen`,
    permanentMessage:
      'Dieses Objekt und alle zugehörigen Bilder werden endgültig aus dem Speicher gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
    permanentSubmit: 'Endgültig löschen',
  },
  trash: 'Papierkorb',
  /** Counted noun under the list while the bin is open. */
  trashCountUnit: `${TERMS.property}e im Papierkorb`,
  restore: {
    title: `${TERMS.property} wiederherstellen`,
    message:
      'Dieses Objekt wird aus dem Papierkorb wiederhergestellt und ist wieder sichtbar.',
  },
  notesHeading: `${TERMS.property}-${FIELD_LABELS.notes.label}`,
  // The id the portal the listing came from uses, not ours.
  listingId: (id: string) => `ID: ${id}`,
} as const
